import { useRef, useState, useMemo } from 'react';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  Popover,
  Typography
} from '@mui/material';

import { styled, useTheme } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import {
  red, pink, purple, deepPurple, indigo, blue,
  lightBlue, cyan, teal, green, lightGreen, lime,
  yellow, amber, orange, deepOrange
} from '@mui/material/colors';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from 'src/content/auth/slice';
import { useNavigate } from 'react-router';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

// Array of Material UI colors to choose from
const colorOptions = [
  red, pink, purple, deepPurple, indigo, blue,
  lightBlue, cyan, teal, green, lightGreen, lime,
  yellow, amber, orange, deepOrange
];

// Function to generate a consistent color based on name
const getAvatarColor = (name) => {
  if (!name) return deepPurple[500]; // Default color

  // Generate a number from the name string
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Use the hash to pick a color
  const colorIndex = Math.abs(hash) % colorOptions.length;
  const shade = [300, 400, 500, 600, 700][Math.abs(hash) % 5]; // Different shade options

  return colorOptions[colorIndex][shade];
};

function HeaderUserbox() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //@ts-ignore
  const { fullName, consignees, primaryCustomerId } = useSelector((state) => state.Auth.user) || { fullName: 'User' };
  const theme = useTheme();
  
  const user = {
    name: fullName,
    avatar: '/static/images/avatars/1.jpg',
    jobtitle: consignees?.length > 0 ? consignees.find(consignee => consignee.customer_id === primaryCustomerId.toString())?.customer_name : '-'
  };

  // Memoize the avatar color so it doesn't change on re-renders
  const avatarColor = useMemo(() => getAvatarColor(fullName), [fullName]);
  const avatarInitial = fullName ? fullName[0] : 'U';

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSignOut = () => {
    dispatch(logout());
    navigate('/');
    handleClose();
  };

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar sx={{ bgcolor: avatarColor }}>{avatarInitial}</Avatar>
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {user.jobtitle}
            </UserBoxDescription>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar sx={{ bgcolor: avatarColor }}>{avatarInitial}</Avatar>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {user.jobtitle}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button color="primary" fullWidth onClick={handleSignOut} sx={{ color: theme.colors.ckb.error }}>
            <LockOpenTwoToneIcon sx={{ mr: 1, color: theme.colors.ckb.error }} />
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
